import { create } from "zustand";
import type { BroadcastMessage } from "../types/ws";
import { parsePositionsWsData, restSnapshotDeclaresPositions, positionSnapshotQualityError } from "../lib/positionSnapshotEnvelope";
import { usePositionsStore } from "../store/positionsStore";
import { useTradesStore } from "../store/tradesStore";
import { useOrdersStore } from "../store/ordersStore";
import { useBlotterStore } from "../store/blotterStore";
import { useReportStore } from "../store/reportStore";
import { parseCurvesWsData, useCurveSnapshotStore } from "../store/curveSnapshotStore";
import type { TradeResponse, BlotterEntry, OrderEntry } from "../types/trades";
import { useAuthStore } from "../auth/authStore";
import { invalidateWsQueries } from "./querySync";
import { useAmendmentReplayStore } from "../store/amendmentReplayStore";
import { useCashBalanceStore } from "../modules/cash/store/cashBalanceStore";
import { useOcpStore } from "../modules/cash/store/ocpStore";
import { usePaymentOrderStore } from "../modules/cash/store/paymentOrderStore";
import type { CashBalance, CashBalanceKey, OcpPosition, OcpKey, PaymentOrder } from "../modules/cash/types/cash";
import {
  scopeAdmits,
  sameScope,
  usePositionsScopeStore,
  type PositionsScope,
} from "../store/positionsScopeStore";
import { useDashboardDisplayStore } from "../store/dashboardDisplayStore";

export type WsConnectionStatus = "idle" | "connecting" | "connected" | "disconnected" | "error";

interface WsMeta {
  status: WsConnectionStatus;
  lastSeq: number;
  lastTs: string | null;
  error: string | null;
  lastCloseCode: number | null;
  lastCloseReason: string | null;
}

export const useWsStatusStore = create<WsMeta>(() => ({
  status: "idle",
  lastSeq: 0,
  lastTs: null,
  error: null,
  lastCloseCode: null,
  lastCloseReason: null,
}));

function parseTradesData(data: unknown): { trades: TradeResponse[]; removed: string[] } {
  if (!data || typeof data !== "object") return { trades: [], removed: [] };
  const o = data as Record<string, unknown>;
  const trades = Array.isArray(o.trades) ? (o.trades as TradeResponse[]) : [];
  const removed = Array.isArray(o.removed) ? (o.removed as string[]).filter((x) => typeof x === "string") : [];
  return { trades, removed };
}

function parseBlotterData(data: unknown): { entries: BlotterEntry[]; removed: string[] } {
  if (!data || typeof data !== "object") return { entries: [], removed: [] };
  const o = data as Record<string, unknown>;
  const entries = Array.isArray(o.entries) ? (o.entries as BlotterEntry[]) : [];
  const removed = Array.isArray(o.removed) ? (o.removed as string[]).filter((x) => typeof x === "string") : [];
  return { entries, removed };
}

function parseOrdersData(data: unknown): { orders: OrderEntry[]; removed: string[] } {
  if (!data || typeof data !== "object") return { orders: [], removed: [] };
  const o = data as Record<string, unknown>;
  const orders = Array.isArray(o.orders) ? (o.orders as OrderEntry[]) : [];
  const removed = Array.isArray(o.removed) ? (o.removed as string[]).filter((x) => typeof x === "string") : [];
  return { orders, removed };
}

export function dispatchMessage(msg: BroadcastMessage) {
  const kind = msg.kind;
  const mtype = (msg.message_type ?? "snapshot") as "snapshot" | "delta";

  const prevMeta = useWsStatusStore.getState();
  const tsRaw = msg.ts;
  const nextTs =
    typeof tsRaw === "string" && tsRaw.trim() ? tsRaw.trim() : (prevMeta.lastTs ?? null);
  if (typeof msg.seq === "number") {
    useWsStatusStore.setState({ lastSeq: msg.seq, lastTs: nextTs });
  } else {
    useWsStatusStore.setState({ lastTs: nextTs });
  }

  switch (kind) {
    case "heartbeat":
      // Server keepalive — no data to process, just updates lastSeq/lastTs above.
      break;
    case "positions": {
      const quality = (mtype === "snapshot" || restSnapshotDeclaresPositions(msg.data)) ? positionSnapshotQualityError(msg.data) : null;
      if (quality) {
        usePositionsStore.setState({ qualityError: quality });
        invalidateWsQueries(["positions"]);
        break;
      }
      if (mtype === "snapshot") usePositionsStore.setState({ qualityError: null });
      const parsed = parsePositionsWsData(msg.data);
      // The server scopes the stream per connection; this also covers rows sent
      // before it applied a scope change, and servers without scope support.
      const scope = usePositionsScopeStore.getState().scope;
      const positions = parsed.positions.filter((row) => scopeAdmits(scope, row));
      const { removed } = parsed;
      if (mtype === "snapshot") {
        // Failed server reads no longer publish snapshots. An explicit empty
        // full image is authoritative; malformed/error-shaped data is not.
        if (!restSnapshotDeclaresPositions(msg.data)) break;
        usePositionsStore.getState().setSnapshot(positions);
      } else usePositionsStore.getState().applyDelta(positions, removed);
      break;
    }
    case "trades": {
      const { trades, removed } = parseTradesData(msg.data);
      if (mtype === "snapshot") useTradesStore.getState().setSnapshot(trades);
      else useTradesStore.getState().applyDelta(trades, removed);
      break;
    }
    case "blotter": {
      const { entries, removed } = parseBlotterData(msg.data);
      if (mtype === "snapshot") useBlotterStore.getState().setSnapshot(entries);
      else useBlotterStore.getState().applyDelta(entries, removed);
      break;
    }
    case "orders": {
      const { orders, removed } = parseOrdersData(msg.data);
      if (mtype === "snapshot") {
        // The orders snapshot comes from a QuestDB sweep on the broadcaster; an
        // empty sweep (query failure) must not clobber REST `/orders` rows —
        // same guard as the positions channel above.
        const cur = useOrdersStore.getState().orders.size;
        if (orders.length === 0 && cur > 0) break;
        useOrdersStore.getState().setSnapshot(orders);
      } else useOrdersStore.getState().applyDelta(orders, removed);
      break;
    }
    case "curves": {
      const { curves, removed } = parseCurvesWsData(msg.data);
      if (mtype === "snapshot") {
        useCurveSnapshotStore.getState().setSnapshot(curves);
      } else {
        useCurveSnapshotStore.getState().applyDelta(curves, removed);
        // A delta row is a new snapshot version: refresh that curve's open
        // inspector too, not only the list.
        for (const c of curves) {
          invalidateWsQueries(["curve", c.curve_id, "snapshots"]);
          invalidateWsQueries(["curve", c.curve_id, "params"]);
        }
      }
      invalidateWsQueries(["curves"]);
      break;
    }
    case "dividends":
      invalidateWsQueries(["dividends"]);
      break;
    case "md_rate_curve":
      invalidateWsQueries(["curves"]);
      break;
    case "coupons":
      invalidateWsQueries(["coupons"]);
      break;
    case "report_generated": {
      const d = (msg.data ?? {}) as Record<string, unknown>;
      const reportId = typeof d.report_id === "string" ? d.report_id : "";
      if (reportId) {
        useReportStore.getState().applyGenerated({
          reportId,
          runId: typeof d.run_id === "string" ? d.run_id : "",
          entityId: typeof d.entity_id === "string" ? d.entity_id : "",
          deskId: typeof d.desk_id === "string" && d.desk_id ? (d.desk_id as string) : null,
          businessDate: typeof d.business_date === "string" ? d.business_date : "",
          version: typeof d.version === "number" ? d.version : 1,
          sealHash: typeof d.seal_hash_sha256 === "string" ? (d.seal_hash_sha256 as string) : null,
        });
      }
      invalidateWsQueries(["reports", "v2", "catalogue"]);
      break;
    }
    case "report_edit": {
      const d = (msg.data ?? {}) as Record<string, unknown>;
      const runId = typeof d.run_id === "string" ? d.run_id : "";
      const reportId = typeof d.report_id === "string" ? d.report_id : "";
      if (runId) {
        useReportStore.getState().applyEdit({
          runId,
          deskId: typeof d.desk_id === "string" && d.desk_id ? (d.desk_id as string) : null,
          userId: typeof d.user_id === "string" ? d.user_id : "",
          sectionCode: typeof d.section_code === "string" ? d.section_code : "",
          editedAt: typeof d.edited_at === "string" ? (d.edited_at as string) : null,
        });
        // Section pages are keyed by run_id; catalogue row shows edit_count.
        invalidateWsQueries(["reports", "section", runId]);
        invalidateWsQueries(["reports", "v2", "catalogue"]);
        if (reportId) {
          invalidateWsQueries(["reports", "v2", reportId]);
        }
      }
      break;
    }
    case "report_locked": {
      const d = (msg.data ?? {}) as Record<string, unknown>;
      const deskId = typeof d.desk_id === "string" ? d.desk_id : "";
      const date = typeof d.business_date === "string" ? d.business_date : "";
      if (deskId && date) {
        useReportStore.getState().applyLocked({
          deskId,
          businessDate: date,
          lockedAt: typeof d.locked_at === "string" ? (d.locked_at as string) : null,
        });
      }
      break;
    }
    case "cash_balance": {
      // Broadcaster sends `{ cash_balances: [...], removed: [...] }`.
      // Fallback to `upsert` key for backward-compat with mock/test messages.
      const d = msg.data as { cash_balances?: CashBalance[]; upsert?: CashBalance[]; removed?: CashBalanceKey[] };
      const balances = d.cash_balances ?? d.upsert ?? [];
      if (mtype === "snapshot") useCashBalanceStore.getState().setSnapshot(balances);
      else useCashBalanceStore.getState().applyDelta(balances, d.removed ?? []);
      break;
    }
    case "ocp": {
      // Broadcaster sends `{ ocp_positions: [...], removed: [...] }`.
      const d = msg.data as { ocp_positions?: OcpPosition[]; upsert?: OcpPosition[]; removed?: OcpKey[] };
      const positions = d.ocp_positions ?? d.upsert ?? [];
      if (mtype === "snapshot") useOcpStore.getState().setSnapshot(positions);
      else useOcpStore.getState().applyDelta(positions, d.removed ?? []);
      break;
    }
    case "payment_order": {
      // Broadcaster sends `{ payment_orders: [...], removed: [...] }`.
      const d = msg.data as { payment_orders?: PaymentOrder[]; upsert?: PaymentOrder[]; removed?: string[] };
      const orders = d.payment_orders ?? d.upsert ?? [];
      if (mtype === "snapshot") usePaymentOrderStore.getState().setSnapshot(orders);
      else usePaymentOrderStore.getState().applyDelta(orders, d.removed ?? []);
      invalidateWsQueries(["payment-orders"]);
      break;
    }
    // ── E3: Dual Settlement Status ─────────────────────────────────────────────
    case "period_pnl": {
      const d = msg.data as {
        positions?: Array<Record<string, string | null | undefined>>;
      };
      if (Array.isArray(d?.positions) && d.positions.length > 0) {
        usePositionsStore.getState().applyPeriodPnlRefresh(d.positions);
      }
      invalidateWsQueries(["positions", "summary"]);
      // The BFF dropped patches it could not convert to our display currency:
      // take the converted values from REST instead.
      if ((msg.data as { display_refetch?: boolean } | undefined)?.display_refetch) {
        invalidateWsQueries(["positions", "snapshot"]);
      }
      break;
    }
    case "position_refresh": {
      const d = msg.data as {
        positions?: import("../types/positions").SnapshotPosition[];
        reason?: string;
        entity_id?: string;
      };
      if (Array.isArray(d?.positions) && d.positions.length > 0) {
        usePositionsStore.getState().applyDelta(d.positions, []);
      }
      invalidateWsQueries(["positions", "snapshot"]);
      invalidateWsQueries(["trades"]);
      invalidateWsQueries(["blotter"]);
      if (d?.reason === "amendment_replay_completed") {
        invalidateWsQueries(["cash"]);
      }
      break;
    }
    case "settlement_linked_transfer_update": {
      invalidateWsQueries(["settlement", "linked-transfers"]);
      break;
    }
    case "amendment_replay_progress": {
      useAmendmentReplayStore.getState().applyProgress(msg.data);
      break;
    }
    case "amendment_replay_completed": {
      useAmendmentReplayStore.getState().applyCompleted(msg.data);
      invalidateWsQueries(["positions", "snapshot"]);
      invalidateWsQueries(["trades"]);
      invalidateWsQueries(["blotter"]);
      invalidateWsQueries(["cash"]);
      break;
    }
    // ── E5: Manual Price Model ─────────────────────────────────────────────────
    case "manual_price_pending": {
      // BFF published a ManualPricePendingApproval NATS event forwarded over WS.
      // Invalidate the pending prices list so the Pending Prices panel refreshes.
      invalidateWsQueries(["refdata", "manual-prices", "pending"]);
      break;
    }
    case "pricing_mode_changed": {
      // BFF published an InstrumentPricingModeChanged NATS event forwarded over WS.
      // Invalidate instruments list and positions snapshot so mode column and blotter badge refresh.
      invalidateWsQueries(["refdata", "instruments"]);
      invalidateWsQueries(["positions", "snapshot"]);
      break;
    }
    default:
      break;
  }
}

/** If no message received in this many ms, consider the connection dead and force reconnect. */
const DEAD_CONNECTION_MS = 45_000;

class AtlasWebSocketClient {
  private ws: WebSocket | null = null;
  private reconnect = 1000;
  private maxReconnect = 30_000;
  /** Monotonic socket generation; ignores stale lifecycle events after reconnect/teardown. */
  private connGen = 0;
  private reconnectTimer: number | null = null;
  private closingForLogout = false;
  /** Watchdog: timestamp of last received message. Used to detect silent dead connections. */
  private lastMessageAt = 0;
  private deadTimer: number | null = null;

  /** Positions rows this connection asks the server for (see positionsScopeStore). */
  private positionsScope: PositionsScope = usePositionsScopeStore.getState().scope;
  /**
   * Currency the server converts live position rows to. It must match the REST
   * snapshot's `display_ccy`, or each tick overwrites converted rows with
   * book-currency ones and the grid blinks between the two.
   */
  private displayCcy: string = useDashboardDisplayStore.getState().displayCcy;

  /**
   * Narrow or widen the positions stream. Sent on the open socket (the server
   * answers with a full positions image in the new scope) and carried in the
   * URL of every later reconnect.
   */
  setPositionsScope(scope: PositionsScope): void {
    if (sameScope(scope, this.positionsScope)) return;
    this.positionsScope = scope;
    this.sendPositionsScope();
  }

  /** Switch the live stream's display currency; same delivery as the scope. */
  setDisplayCcy(ccy: string): void {
    if (ccy === this.displayCcy) return;
    this.displayCcy = ccy;
    this.sendPositionsScope();
  }

  private sendPositionsScope(): void {
    const ws = this.ws;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: "scope",
        stream: "positions",
        ...this.positionsScope,
        display_ccy: this.displayCcy,
      }));
    }
  }

  connect(): void {
    const token = useAuthStore.getState().accessToken;
    if (!token) return;

    this.closingForLogout = false;
    const generation = ++this.connGen;

    const previous = this.ws;
    this.ws = null;
    if (previous) {
      previous.onopen = null;
      previous.onclose = null;
      previous.onerror = null;
      previous.onmessage = null;
      try { previous.close(); } catch { /* ignore */ }
    }

    this.stopDeadTimer();

    useWsStatusStore.setState({
      status: "connecting",
      error: null,
      lastSeq: 0,
      lastTs: null,
      lastCloseCode: null,
      lastCloseReason: null,
    });

    // Fetch a short-lived one-time ticket so the JWT never appears in URL / nginx logs.
    void this.connectWithTicket(token, generation);
  }

  private async connectWithTicket(token: string, generation: number): Promise<void> {
    let wsParam: string;
    let paramName: string;

    try {
      const res = await fetch("/api/v1/auth/ws-ticket", {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`ws-ticket ${res.status}`);
      const data = (await res.json()) as { ticket: string };
      wsParam = data.ticket;
      paramName = "ticket";
    } catch {
      // Fall back to direct token if ticket endpoint unavailable (e.g. rolling deploy).
      wsParam = token;
      paramName = "token";
    }

    if (generation !== this.connGen) return; // Superseded by a newer connect() call.

    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${window.location.host}/ws?${paramName}=${encodeURIComponent(wsParam)}${positionsScopeUrlParams(this.positionsScope)}${displayCcyUrlParam(this.displayCcy)}`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      useWsStatusStore.setState({
        status: "error",
        error: e instanceof Error ? e.message : "WebSocket ctor failed",
      });
      this.scheduleReconnect();
      return;
    }

    this.ws = ws;

    ws.onopen = () => {
      if (generation !== this.connGen) return;
      this.reconnect = 1000;
      this.lastMessageAt = Date.now();
      useWsStatusStore.setState({ status: "connected", error: null });
      this.startDeadTimer(generation);
    };

    ws.onclose = (ev) => {
      if (generation !== this.connGen) return;
      this.stopDeadTimer();
      if (this.closingForLogout) {
        useWsStatusStore.setState({ status: "idle", lastCloseCode: null, lastCloseReason: null });
        return;
      }
      // 1000 = normal, 1005 = no status (browser), 1006 = abnormal (proxy / TLS / auth reject)
      const abnormal = ev.code !== 1000 && ev.code !== 1005;
      const detail = abnormal
        ? `WS closed ${ev.code}${ev.reason ? `: ${ev.reason}` : ""}`
        : null;
      useWsStatusStore.setState({
        status: "disconnected",
        error: detail,
        lastCloseCode: ev.code,
        lastCloseReason: ev.reason ? ev.reason : null,
      });
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      if (generation !== this.connGen) return;
      useWsStatusStore.setState({ status: "error", error: "WebSocket error" });
    };

    ws.onmessage = (ev) => {
      if (generation !== this.connGen) return;
      this.lastMessageAt = Date.now();
      try {
        const msg = JSON.parse(String(ev.data)) as BroadcastMessage;
        dispatchMessage(msg);
      } catch {
        /* ignore malformed */
      }
    };
  }

  /** Periodically check if we've gone silent — if so, the connection is dead. */
  private startDeadTimer(generation: number): void {
    this.stopDeadTimer();
    this.deadTimer = window.setInterval(() => {
      if (generation !== this.connGen) {
        this.stopDeadTimer();
        return;
      }
      if (Date.now() - this.lastMessageAt > DEAD_CONNECTION_MS) {
        console.warn("[AtlasWS] No message for", DEAD_CONNECTION_MS / 1000, "s — reconnecting");
        useWsStatusStore.setState({ status: "disconnected", error: "Connection silent — reconnecting" });
        this.stopDeadTimer();
        this.connect();
      }
    }, 10_000);
  }

  private stopDeadTimer(): void {
    if (this.deadTimer !== null) {
      window.clearInterval(this.deadTimer);
      this.deadTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    const delay = this.reconnect;
    this.reconnect = Math.min(this.reconnect * 2, this.maxReconnect);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (!useAuthStore.getState().accessToken) return;
      this.connect();
    }, delay);
  }

  /** Reconnect immediately if currently disconnected (e.g. on tab focus). */
  reconnectIfNeeded(): void {
    const status = useWsStatusStore.getState().status;
    if (status === "disconnected" || status === "error") {
      this.reconnect = 1000;
      this.connect();
    }
  }

  close(): void {
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopDeadTimer();
    this.closingForLogout = true;
    this.connGen++;
    const w = this.ws;
    this.ws = null;
    if (w) {
      w.onopen = null;
      w.onclose = null;
      w.onerror = null;
      w.onmessage = null;
      try { w.close(); } catch { /* ignore */ }
    }
    useWsStatusStore.setState({
      status: "idle",
      error: null,
      lastSeq: 0,
      lastTs: null,
      lastCloseCode: null,
      lastCloseReason: null,
    });
  }
}

export const atlasWs = new AtlasWebSocketClient();

function positionsScopeUrlParams(scope: PositionsScope): string {
  const parts: string[] = [];
  if (scope.books.length) parts.push(`books=${encodeURIComponent(scope.books.join(","))}`);
  if (scope.entities.length) parts.push(`entities=${encodeURIComponent(scope.entities.join(","))}`);
  if (scope.accounts.length) parts.push(`accounts=${encodeURIComponent(scope.accounts.join(","))}`);
  return parts.length ? `&${parts.join("&")}` : "";
}

function displayCcyUrlParam(ccy: string): string {
  const trimmed = ccy.trim();
  return trimmed ? `&display_ccy=${encodeURIComponent(trimmed)}` : "";
}

// Follow the dashboard's display currency: the REST snapshot refetches under
// its new query key, and the live stream converts to the same currency.
useDashboardDisplayStore.subscribe((state, prev) => {
  if (state.displayCcy === prev.displayCcy) return;
  atlasWs.setDisplayCcy(state.displayCcy);
});

// Follow the dashboard's book scope: drop rows outside it at once (no stale
// rows from the previous scope) and re-scope the live stream.
usePositionsScopeStore.subscribe((state, prev) => {
  if (sameScope(state.scope, prev.scope)) return;
  const scope = state.scope;
  usePositionsStore.getState().retainWhere((row) => scopeAdmits(scope, row));
  atlasWs.setPositionsScope(scope);
});

// Reconnect when the tab becomes visible again after being hidden
// (browsers may have suspended the WS while hidden).
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      atlasWs.reconnectIfNeeded();
    }
  });
}

// Reconnect when the network comes back online.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    atlasWs.reconnectIfNeeded();
  });
}
